import {
  ArrowRight,
  BookOpen,
  Calculator,
  CheckCircle2,
  ChevronLeft,
  Clock3,
  FileText,
  MessageCircle,
  Sigma,
  Target,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const unit12Topics = [
  "Functions, relations, graphs, transformations, domain, range, and inverses.",
  "Algebraic techniques including polynomials, simultaneous equations, and symbolic fluency.",
  "Introductory calculus: rates of change, basic differentiation, anti-differentiation, and applied interpretation.",
  "Probability and statistics foundations including counting principles, conditional probability, and data reasoning.",
];

const unit34Topics = [
  "Composite, hybrid, and transformed functions used in more complex modelling tasks.",
  "Advanced differentiation with product, quotient, and chain rules applied to optimisation and curve behaviour.",
  "Integration, trapezium-rule style approximation, area, volume, and connected exam-style applications.",
  "Probability and statistics with random variables, distributions, sampling, and high-level CAS-ready problem solving.",
];

const resources = [
  "Access to the Ascend Academy student website with exclusive resources, official exam walkthroughs, practice SAC walkthroughs, useful CAS skills videos, performance guides, worked solutions, and VCAA exam materials.",
  "Practice SACs and carefully sequenced exam-style checkpoints.",
  "Practice exams that train speed, structure, and method selection under pressure.",
  "Homework question sets targeting fluency, adaptation, and retention.",
  "In-depth content explanation with structured notes and worked examples.",
];

const services = [
  "Weekly online content sessions that teach each topic with clarity and precision.",
  "Remote applied sessions focused on SAC-style and exam-style questions under timed conditions.",
  "Live in-person trial exams near the exam period with in-depth marking, projections, and feedback.",
  "24/7 tutor access for questions related to course material, revision, and problem solving.",
  "Included access to the Ascend Academy website platform and Ascend Academy AI for immediate explanations when students are stuck on Methods content.",
];

const platformFeatures = [
  "Exclusive Mathematical Methods resources organised by topic and assessment need.",
  "Official exam walkthroughs, practice SAC walkthroughs, CAS skills videos, and performance guides for Methods students.",
  "Worked solutions, practice SACs, and VCAA exam material students can revisit anytime.",
  "Ascend Academy AI for immediate explanations when students need help on Methods questions.",
];

export const MathMethodsPage = () => {
  return (
    <div className="ascend-shell bg-background text-[#10233d]" data-testid="math-methods-page">
      <header className="border-b border-[#002147]/10 bg-[#f9f6f0]/90 backdrop-blur-xl" data-testid="math-methods-header">
        <div className="ascend-container flex flex-wrap items-center justify-between gap-4 py-4">
          <Link to="/" className="font-display text-2xl font-semibold tracking-tight text-[#002147]" data-testid="math-methods-home-link">
            Ascend Academy
          </Link>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="math-methods-back-button">
              <Link to="/">
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back to Home
              </Link>
            </Button>
            <Button asChild className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="math-methods-cta-button">
              <a href="/#inquiry">Request an Information Session</a>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="ascend-section border-b border-[#002147]/10" data-testid="math-methods-hero">
          <div className="ascend-container grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="space-y-6">
              <p className="eyebrow text-[#002147]/65" data-testid="math-methods-overline">Subject-specific program</p>
              <h1 className="font-display text-4xl tracking-tight text-[#002147] sm:text-5xl lg:text-6xl" data-testid="math-methods-heading">
                VCE Mathematical Methods
              </h1>
              <p className="max-w-3xl text-sm leading-7 text-slate-700 md:text-base" data-testid="math-methods-description">
                The Ascend Mathematical Methods program is built for students who want conceptual command, strong SAC execution, and the disciplined routines needed for high-end exam performance.
                The page below outlines the content coverage, the resources students receive, and the support systems used to maximise Methods outcomes.
              </p>
            </div>

            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="math-methods-summary-card">
              <CardContent className="space-y-5 p-8">
                <p className="eyebrow text-[#f3d8a1]" data-testid="math-methods-summary-overline">What students receive</p>
                <div className="grid gap-4">
                  {[
                    { label: "Structured notes", icon: BookOpen },
                    { label: "Exam conditioning", icon: Target },
                    { label: "Ascend Academy platform", icon: FileText },
                    { label: "24/7 tutor access", icon: MessageCircle },
                  ].map((item) => {
                    const Icon = item.icon;

                    return (
                      <div key={item.label} className="flex items-center gap-3 border border-white/12 bg-white/5 p-4" data-testid={`math-methods-summary-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <Icon className="h-4 w-4 text-[#f3d8a1]" />
                        <span className="text-sm text-white/86">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="ascend-section paper-grid" data-testid="math-methods-units-section">
          <div className="ascend-container grid gap-6 lg:grid-cols-2">
            <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="math-methods-unit12-card">
              <CardContent className="space-y-6 p-8">
                <div className="flex items-center gap-3">
                  <Calculator className="h-5 w-5 text-[#002147]" />
                  <h2 className="font-display text-3xl text-[#002147]" data-testid="math-methods-unit12-heading">Units 1/2 coverage</h2>
                </div>
                <ul className="grid gap-3 text-sm leading-7 text-slate-700" data-testid="math-methods-unit12-list">
                  {unit12Topics.map((topic) => (
                    <li key={topic} className="flex gap-3" data-testid={`math-methods-unit12-topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="math-methods-unit34-card">
              <CardContent className="space-y-6 p-8">
                <div className="flex items-center gap-3">
                  <Sigma className="h-5 w-5 text-[#f3d8a1]" />
                  <h2 className="font-display text-3xl text-white" data-testid="math-methods-unit34-heading">Units 3/4 coverage</h2>
                </div>
                <ul className="grid gap-3 text-sm leading-7 text-white/84" data-testid="math-methods-unit34-list">
                  {unit34Topics.map((topic) => (
                    <li key={topic} className="flex gap-3" data-testid={`math-methods-unit34-topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="ascend-section bg-[#f4efe5]" data-testid="math-methods-resources-services">
          <div className="ascend-container grid gap-6 lg:grid-cols-[1fr_1fr]">
            <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="math-methods-resources-card">
              <CardContent className="space-y-6 p-8">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-[#002147]" />
                  <h2 className="font-display text-3xl text-[#002147]" data-testid="math-methods-resources-heading">Resources provided</h2>
                </div>
                <ul className="grid gap-3 text-sm leading-7 text-slate-700" data-testid="math-methods-resources-list">
                  {resources.map((resource) => (
                    <li key={resource} className="flex gap-3" data-testid={`math-methods-resource-${resource.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                      <span>{resource}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="math-methods-services-card">
              <CardContent className="space-y-6 p-8">
                <div className="flex items-center gap-3">
                  <Clock3 className="h-5 w-5 text-[#002147]" />
                  <h2 className="font-display text-3xl text-[#002147]" data-testid="math-methods-services-heading">Services provided</h2>
                </div>
                <ul className="grid gap-3 text-sm leading-7 text-slate-700" data-testid="math-methods-services-list">
                  {services.map((service) => (
                    <li key={service} className="flex gap-3" data-testid={`math-methods-service-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="ascend-section" data-testid="math-methods-platform-section">
          <div className="ascend-container">
            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="math-methods-platform-card">
              <CardContent className="grid gap-6 p-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
                <div className="space-y-4">
                  <p className="eyebrow text-[#f3d8a1]" data-testid="math-methods-platform-overline">Ascend Academy Platform</p>
                  <h2 className="font-display text-3xl text-white md:text-4xl" data-testid="math-methods-platform-heading">
                    A dedicated Methods support website built into the Ascend experience.
                  </h2>
                  <p className="text-sm leading-7 text-white/82" data-testid="math-methods-platform-description">
                    The platform gives Methods students one place to access weekly homework, practice SACs, practice exams, official walkthrough videos, CAS skills guides, worked solutions, and immediate explanations when questions arise.
                  </p>
                  <Button asChild className="rounded-sm bg-[#c5a059] text-[#002147] hover:bg-[#b48d4d]" data-testid="math-methods-platform-cta">
                    <a href="/#inquiry">
                      Ask About the Platform
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                </div>
                <div className="grid gap-4" data-testid="math-methods-platform-list">
                  {platformFeatures.map((feature) => (
                    <div key={feature} className="flex gap-3 border border-white/12 bg-white/5 p-4" data-testid={`math-methods-platform-feature-${feature.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" />
                      <p className="text-sm leading-7 text-white/84">{feature}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="ascend-section" data-testid="math-methods-cta-section">
          <div className="ascend-container">
            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="math-methods-cta-card">
              <CardContent className="flex flex-col gap-6 p-8 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-3">
                  <p className="eyebrow text-[#f3d8a1]" data-testid="math-methods-cta-overline">Next step</p>
                  <h2 className="font-display text-3xl text-white" data-testid="math-methods-cta-heading">See whether the Ascend Methods pathway is the right fit.</h2>
                  <p className="max-w-3xl text-sm leading-7 text-white/82" data-testid="math-methods-cta-copy">
                    Enquire for a free information session to explore the program, workload expectations, and how the weekly training model supports strong Mathematical Methods outcomes.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="rounded-sm bg-[#c5a059] text-[#002147] hover:bg-[#b48d4d]" data-testid="math-methods-request-button">
                    <a href="/#inquiry">
                      Request Information Session
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="rounded-sm border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white" data-testid="math-methods-specialist-link">
                    <Link to="/specialist-maths">View Specialist Maths</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
};